import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  DialogContentText,
} from '@mui/material';
import {
  Assignment,
  Home,
  Add,
  Delete,
  DragIndicator,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

interface InstructionType {
  id: number;
  name: string;
  description: string | null;
  sample_value: string | null;
  reference_image: string | null;
  sort_order: number | null;
}

interface NewInstructionTypeForm {
  name: string;
  description: string;
  sample_value: string;
  reference_image: File | null;
}

export const CustomReportInstructionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InstructionType | null>(null);
  const [formData, setFormData] = useState<NewInstructionTypeForm>({
    name: '',
    description: '',
    sample_value: '',
    reference_image: null,
  });
  const [submitting, setSubmitting] = useState(false);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<InstructionType | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);

  // Drag and drop state for table rows
  const [draggedItem, setDraggedItem] = useState<InstructionType | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [updatingOrder, setUpdatingOrder] = useState(false);

  const { data: instructionTypes, isLoading, error, refetch } = useQuery({
    queryKey: ['instruction-types'],
    queryFn: async (): Promise<InstructionType[]> => {
      const response = await authenticatedFetch('/api/marketcompass/admin/trend-report-instruction-types');
      if (!response || !response.ok) {
        throw new Error('Failed to fetch instruction types');
      }
      const result = await response.json();
      return result.data;
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      sample_value: '',
      reference_image: null,
    });
    setImagePreview(null);
    setIsDragOver(false);
    setEditingItem(null);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleEditClick = (instruction: InstructionType) => {
    setEditingItem(instruction);
    setFormData({
      name: instruction.name,
      description: instruction.description || '',
      sample_value: instruction.sample_value || '',
      reference_image: null, // Don't pre-load existing image file
    });
    // Set image preview to existing image URL
    if (instruction.reference_image) {
      setImagePreview(instruction.reference_image);
    }
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('sample_value', formData.sample_value);

      if (editingItem) {
        // Edit mode
        formDataToSend.append('sort_order', (editingItem.sort_order || 0).toString());
        if (formData.reference_image) {
          formDataToSend.append('reference_image', formData.reference_image);
        }

        const response = await authenticatedFetch(`/api/marketcompass/admin/trend-report-instruction-types/${editingItem.id}`, {
          method: 'PUT',
          body: formDataToSend,
        });

        if (!response || !response.ok) {
          throw new Error('Failed to update instruction type');
        }
      } else {
        // Create mode
        // Calculate next sort order
        const maxSortOrder = instructionTypes?.reduce((max, item) => {
          const sortOrder = item.sort_order || 0;
          return sortOrder > max ? sortOrder : max;
        }, 0) || 0;
        const nextSortOrder = maxSortOrder + 1;

        formDataToSend.append('sort_order', nextSortOrder.toString());
        if (formData.reference_image) {
          formDataToSend.append('reference_image', formData.reference_image);
        }

        const response = await authenticatedFetch('/api/marketcompass/admin/trend-report-instruction-types', {
          method: 'POST',
          body: formDataToSend,
        });

        if (!response || !response.ok) {
          throw new Error('Failed to create instruction type');
        }
      }

      handleModalClose();
      refetch();
    } catch (error) {
      console.error(`Error ${editingItem ? 'updating' : 'creating'} instruction type:`, error);
      // TODO: Add proper error handling/toast
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (instruction: InstructionType) => {
    setItemToDelete(instruction);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setDeleting(true);
    try {
      const response = await authenticatedFetch(`/api/marketcompass/admin/trend-report-instruction-types/${itemToDelete.id}`, {
        method: 'DELETE',
      });

      if (!response || !response.ok) {
        throw new Error('Failed to delete instruction type');
      }

      setDeleteModalOpen(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      console.error('Error deleting instruction type:', error);
      // TODO: Add proper error handling/toast
    } finally {
      setDeleting(false);
    }
  };

  const handleFileSelect = (file: File | null) => {
    setFormData({ ...formData, reference_image: file });

    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        handleFileSelect(file);
      }
    }
  };

  const handleClickUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0] || null;
      handleFileSelect(file);
    };
    input.click();
  };

  // Table row drag and drop handlers
  const handleRowDragStart = (e: React.DragEvent<HTMLTableRowElement>, item: InstructionType) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRowDragOver = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleRowDragLeave = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleRowDrop = async (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedItem || !instructionTypes) return;

    const dragIndex = instructionTypes.findIndex(item => item.id === draggedItem.id);
    if (dragIndex === dropIndex) return;

    // Create new array with reordered items
    const newItems = [...instructionTypes];
    const [removed] = newItems.splice(dragIndex, 1);
    newItems.splice(dropIndex, 0, removed);

    // Update sort orders
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      sort_order: index + 1
    }));

    // Update sort orders in database
    await updateSortOrders(updatedItems);
  };

  const handleRowDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const updateSortOrders = async (items: InstructionType[]) => {
    setUpdatingOrder(true);
    try {
      const updates = items.map(item => ({
        id: item.id,
        sort_order: item.sort_order
      }));

      const response = await authenticatedFetch('/api/marketcompass/admin/trend-report-instruction-types/reorder', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (!response || !response.ok) {
        throw new Error('Failed to update sort orders');
      }

      refetch();
    } catch (error) {
      console.error('Error updating sort orders:', error);
      // TODO: Add proper error handling/toast
    } finally {
      setUpdatingOrder(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Custom Report Instructions</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Assignment sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Custom Report Instructions
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Guidelines for creating and delivering custom reports to clients
          </Typography>
        </Box>

        {/* Instruction Types Table */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Instruction Types
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setModalOpen(true)}
            >
              Instruction Type
            </Button>
          </Box>

          {(isLoading || updatingOrder) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              {updatingOrder && (
                <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                  Updating order...
                </Typography>
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Failed to load instruction types: {error instanceof Error ? error.message : 'Unknown error'}
            </Alert>
          )}

          {instructionTypes && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="40" sx={{ padding: '8px' }}></TableCell>
                    <TableCell>Sort Order</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Reference Image</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {instructionTypes.map((instruction, index) => (
                    <TableRow
                      key={instruction.id}
                      draggable
                      onDragStart={(e) => handleRowDragStart(e, instruction)}
                      onDragOver={(e) => handleRowDragOver(e, index)}
                      onDragLeave={handleRowDragLeave}
                      onDrop={(e) => handleRowDrop(e, index)}
                      onDragEnd={handleRowDragEnd}
                      sx={{
                        cursor: 'move',
                        opacity: draggedItem?.id === instruction.id ? 0.5 : 1,
                        backgroundColor: dragOverIndex === index ? '#f5f5f5' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: dragOverIndex === index ? '#f5f5f5' : '#fafafa'
                        }
                      }}
                    >
                      <TableCell sx={{ padding: '8px', textAlign: 'center' }}>
                        <DragIndicator
                          sx={{
                            color: 'rgba(0, 0, 0, 0.4)',
                            fontSize: '20px',
                            cursor: 'grab',
                            '&:active': {
                              cursor: 'grabbing'
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>{instruction.sort_order || '-'}</TableCell>
                      <TableCell>{instruction.name}</TableCell>
                      <TableCell>{instruction.description || '-'}</TableCell>
                      <TableCell>
                        {instruction.reference_image ? (
                          <img
                            src={instruction.reference_image}
                            alt={`Reference for ${instruction.name}`}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #e0e0e0',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSelectedImage({
                                url: instruction.reference_image!,
                                name: instruction.name
                              });
                              setImageModalOpen(true);
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit instruction type">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(instruction)}
                            size="small"
                            sx={{ mr: 1 }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete instruction type">
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(instruction)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Create/Edit Instruction Type Modal */}
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
          <DialogTitle>{editingItem ? 'Edit Instruction Type' : 'Create New Instruction Type'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Sample Value"
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.sample_value}
                  onChange={(e) => setFormData({ ...formData, sample_value: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Reference Image
                </Typography>
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleClickUpload}
                  sx={{
                    border: isDragOver ? '2px dashed #1976d2' : '2px dashed #ccc',
                    borderRadius: '8px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragOver ? '#f5f5f5' : '#fafafa',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#1976d2',
                      backgroundColor: '#f5f5f5',
                    },
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {imagePreview ? (
                    <>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: '100px',
                          maxHeight: '100px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {formData.reference_image?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {editingItem ? 'Click or drag to replace existing image' : 'Click or drag to replace'}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" color="text.secondary">
                        {editingItem && editingItem.reference_image
                          ? 'Current image will be preserved. Drag and drop to replace, or click to browse'
                          : 'Drag and drop an image here, or click to browse'
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supports: JPEG, PNG, GIF, WebP (max 10MB)
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.name.trim() || submitting}
            >
              {submitting ? (editingItem ? 'Updating...' : 'Creating...') : (editingItem ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onClose={handleDeleteCancel}>
          <DialogTitle>Delete Instruction Type</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the instruction type "{itemToDelete?.name}"?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image View Modal */}
        <Dialog
          open={imageModalOpen}
          onClose={() => setImageModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Reference Image - {selectedImage?.name}
          </DialogTitle>
          <DialogContent>
            {selectedImage && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <img
                  src={selectedImage.url}
                  alt={`Full size reference for ${selectedImage.name}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '70vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImageModalOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
};